/**
 * Hook to handle AI subscription checkout with §312j/§356 BGB consent.
 *
 * Returns:
 * - `openConsentDialog()` — call this to open the consent dialog before checkout
 * - `consentDialogProps` — spread these into a PurchaseConfirmDialog component
 *
 * Consumer must render: `<PurchaseConfirmDialog {...consentDialogProps} />`
 */
import { generateSubscriptionCallbackLink } from '@affine/core/components/hooks/affine/use-subscription-notify';
import {
  AuthService,
  GraphQLService,
  SubscriptionService,
} from '@affine/core/modules/cloud';
import { UrlService } from '@affine/core/modules/url';
import {
  type PricingPlansQuery,
  pricingPlansQuery,
  SubscriptionRecurring,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useFramework } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { buildSubscriptionOrderSummary } from '../../consent';

export const useAISubscribe = () => {
  const framework = useFramework();
  const t = useI18n();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [pricingPlans, setPricingPlans] = useState<
    PricingPlansQuery['pricingPlans'] | null
  >(null);
  const lang = t.language?.startsWith('en') ? 'en' : 'de';

  useEffect(() => {
    const graphQLService = framework.get(GraphQLService);
    graphQLService
      .gql({
        query: pricingPlansQuery,
        variables: { language: lang },
      })
      .then((res: PricingPlansQuery) => setPricingPlans(res.pricingPlans))
      .catch(() => setPricingPlans(null));
  }, [framework, lang]);

  const orderSummary = useMemo(() => {
    const plan = pricingPlans?.find(p => p.productName === 'Pro');
    const price = plan?.prices.find(p => p.interval === 'yearly');

    if (!plan || !price) {
      return undefined;
    }

    return buildSubscriptionOrderSummary({
      amountCents: price.amount,
      currency: price.currency,
      interval: 'yearly',
      language: lang,
      productName: plan.productName,
    });
  }, [lang, pricingPlans]);

  const executeCheckout = useCallback(async () => {
    if (!orderSummary) {
      return;
    }

    setIsRedirecting(true);
    try {
      const authService = framework.get(AuthService);
      const subscriptionService = framework.get(SubscriptionService);
      const urlService = framework.get(UrlService);

      const account = authService.session.account$.value;
      if (!account) {
        return;
      }

      const session = await subscriptionService.createCheckoutSession({
        recurring: SubscriptionRecurring.Yearly,
        plan: 'Pro',
        variant: null,
        coupon: null,
        withdrawalConsent: true,
        successCallbackLink: generateSubscriptionCallbackLink(
          account,
          'Pro',
          SubscriptionRecurring.Yearly
        ),
      });

      urlService.openExternal(session);
    } catch (error) {
      console.error(error);
    } finally {
      setIsRedirecting(false);
      setShowConfirm(false);
    }
  }, [framework, orderSummary]);

  const openConsentDialog = useCallback(async () => {
    setShowConfirm(true);
  }, []);

  const consentDialogProps = {
    open: showConfirm,
    onOpenChange: (open: boolean) => {
      if (!open) setShowConfirm(false);
    },
    productName: 'Pro Plan',
    priceLabel: 'Yearly',
    orderSummary,
    isSubscription: true as const,
    isLoading: isRedirecting || !orderSummary,
    onConfirm: () => void executeCheckout(),
  };

  return { openConsentDialog, consentDialogProps };
};
